import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "../../../../../assets/images/icons/DeleteIcon";
import DownloadIcon from "../../../../../assets/images/icons/DownloadIcon";
import FullscreenIcon from "../../../../../assets/images/icons/Image/FullscreenIcon";
import BriaTooltip from "../../../../../components/common/BriaTooltip/BriaTooltip";
import { BriaImageProps } from "../../../../../components/common/Galleries/BriaImage.tsx";
import ScrollableGallery from "../../../../../components/common/Galleries/ScrollableGallery";
import { useAppStore } from "../../../../../hooks/useStores";
import { PlaygroundImage, PlaygroundResult } from "../../../../../models/image-to-image";
import { isAutomotiveApp, isFoxApps } from "../../../../../utils/index.ts";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";
import PlaygroundImageOverlay from "../../../Images/Overlay/PlaygroundImageOverlay";
import styles from "./UploadedOrSavedResult.module.scss";

type Props = {
	playgroundResult: PlaygroundResult;
	label?: string;
	rowIndex?: number;
	showFeedbackTutorialOverlay?: boolean;
};

const UploadedOrSavedResult = ({
	playgroundResult,
	label = "upload",
	rowIndex,
	showFeedbackTutorialOverlay = false,
}: Props) => {
	const { t } = useTranslation("translation", {
		keyPrefix: label === "save" ? "playground.savedImages.result" : "playground.uploadedImages.result",
	});
	const { playgroundStore, uiStore } = useAppStore();
	const [openFullScreen, setOpenFullScreen] = useState(false);
	const loading = !!playgroundResult.images.filter((image) => image.loading).length;

	const handleDeleteImages = () => {
		playgroundStore.deleteResult(playgroundResult.id);
	};

	const handleDownloadImages = () => {
		uiStore.showExportImagePopup({ images: playgroundResult.images });
	};

	return (
		<Box className={clsx(styles.uploadORSaveGalleryWrapper, "galleryWrapper")}>
			<Box className={styles.header}>
				<Box className={styles.headerText}>{t("header")}</Box>
				<Box className={styles.actionBtns}>
					{(!iframeStore.isIframe() ||
						iframeStore.iframe.config.general_config?.enable_download ||
						iframeStore.iframe.config.general_config?.enable_save) && (
						<BriaTooltip title={t("download")}>
							<DownloadIcon className={styles.iconButton} onClick={handleDownloadImages} />
						</BriaTooltip>
					)}
					<BriaTooltip title={t("delete")}>
						<DeleteIcon className={styles.iconButton} onClick={handleDeleteImages} />
					</BriaTooltip>
					<BriaTooltip title={t("fullScreen")}>
						<FullscreenIcon className={styles.iconButton} onClick={() => setOpenFullScreen(true)} />
					</BriaTooltip>
				</Box>
			</Box>
			<ScrollableGallery
				images={playgroundResult.images.map(
					(image) =>
						({
							...image,
							ImageOverlay: (
								<PlaygroundImageOverlay
									{...{ image }}
									hideAiEditorButton={isAutomotiveApp()}
									highlightFeedbackButtons={showFeedbackTutorialOverlay}
								/>
							),
							displayOverlay: "customHover",
							selectable: true,
							htmlJsonData: image.id,
							fullScreenProps: {
								fileName: image.file?.name.split(".")[0],
							},
							downloadProps: { fileName: playgroundStore.getFileName(image) },
							onSuccessPulling: async () => {
								(isFoxApps() || rowIndex === 0) &&
									playgroundStore.selectImages(playgroundResult.images);
								playgroundStore.onSuccessResult(image);
							},
							async: loading,
						}) as BriaImageProps & PlaygroundImage,
				)}
				fullScreenProps={{
					open: openFullScreen,
					onClose: () => setOpenFullScreen(false),
					displayMode: "scroll",
				}}
				className={styles.gallery}
			/>
		</Box>
	);
};

export default observer(UploadedOrSavedResult);
